import CreatableSelect from "react-select/creatable";
import React, { useEffect, useState } from "react";
import { showDangerToast } from "../../validation/ToastContainer";
import { AUTHORIZATION_VALUES } from "./deviceAuthorizationService";
import { Spinner } from "../../view/Spinner";
import SelectV2 from "../../view/SelectV2";
import { MoreVertical, X } from "lucide-react";
import * as deviceService from "../deviceService";

const RequiredMark = () => <span className="modal-v2-required-mark">*</span>;

function DeviceAuthorizationModal({ devices, currentDeviceId, initialData, onClose, onSave }) {
    const [saving, setSaving] = useState(false);
    const [showActionsInfo, setShowActionsInfo] = useState(false);
    const [selectedDeviceDetails, setSelectedDeviceDetails] = useState(null);
    const [form, setForm] = useState({
        deviceId: currentDeviceId || "",
        userId: "",
        username: "",
        usernames: [],
        actions: ["Any Action"],
        actionAuthorizations: { "Any Action": "ALLOWED" },
        severity: "HIGH",
        alertEnabled: true,
        notifyBy: "EMAIL",
        validFrom: null,
        validTill: null,
        reason: ""
    });

    useEffect(() => {
        if (!initialData) return;
        setForm({
            id: initialData.id,
            deviceId: initialData.deviceId || initialData.devicePk || currentDeviceId || "",
            userId: initialData.userId || initialData.userPk || "",
            username: initialData.username || "",
            usernames: initialData.username ? [initialData.username] : [],
            actions: ["Any Action"],
            actionAuthorizations: {
                "Any Action": initialData.authorization || "ALLOWED"
            },
            severity: initialData.severity || "HIGH",
            alertEnabled: initialData.alertEnabled !== false,
            notifyBy: initialData.notifyBy || "EMAIL",
            validFrom: null,
            validTill: null,
            reason: initialData.reason || ""
        });
    }, [initialData, currentDeviceId]);

    useEffect(() => {
        if (!form.deviceId) {
            setSelectedDeviceDetails(null);
            return;
        }

        let cancelled = false;
        deviceService.loadDeviceById(form.deviceId)
            .then(device => {
                if (!cancelled) {
                    setSelectedDeviceDetails(device);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setSelectedDeviceDetails(null);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [form.deviceId]);

    const deviceOptions = devices.map(device => ({
        value: device.id || device.pk,
        label: `${device.hostName || "Device"}${device.mngtIpAddress ? ` (${device.mngtIpAddress})` : ""}`
    }));
    const selectedDevice = devices.find(device => Number(device.id || device.pk) === Number(form.deviceId));
    const selectedDeviceEvents = selectedDeviceDetails?.events?.length
        ? selectedDeviceDetails.events
        : selectedDevice?.events || [];
    const selectedDeviceActions = Array.from(new Set((selectedDeviceEvents || [])
        .map(event => String(event.name || event.value || "").trim())
        .filter(Boolean)));

    const setValue = (key, value) => setForm(current => ({ ...current, [key]: value }));
    const setActionAuthorization = (action, authorization) => {
        setForm(current => ({
            ...current,
            actionAuthorizations: {
                ...current.actionAuthorizations,
                [action]: authorization
            }
        }));
    };
    const selectedUserOptions = form.usernames.map(username => ({ value: username, label: username }));

    const submit = async () => {
        if (!form.deviceId || !form.usernames.length || !form.actions.length) {
            return showDangerToast("Device, user, and action are required", "Authorization");
        }

        setSaving(true);
        await onSave({
            ...form,
            action: form.actions[0],
            authorization: form.actionAuthorizations[form.actions[0]] || "ALLOWED",
            username: form.usernames[0],
            deviceId: Number(form.deviceId),
            userId: null,
            validFrom: null,
            validTill: null
        });
        setSaving(false);
    };

    return (
        <div className="modal oct_display-block modal-v2-root oct_alert-v2-modal">
            <div className="modal fade oct_modal show modal-v2-overlay">
                <div className="modal-dialog modal-dialog-centered modal-v2-dialog oct_alert-v2-modal-dialog device-auth-rule-dialog" aria-modal="true">
                    <div className="modal-content modal-v2-shell oct_alert-v2-modal-shell">
                        <div className="modal-v2-header oct_alert-v2-modal-header">
                            <div className="modal-v2-header-top">
                                <div className="modal-v2-header-copy">
                                    <h2 className="modal-v2-heading">{initialData ? "Edit Authorization" : "Add Authorization"}</h2>
                                    <p className="modal-v2-subheading">Control user actions for a specific device.</p>
                                </div>
                                <button type="button" className="close modal-v2-close" aria-label="Close" onClick={onClose}>
                                    <X size={18} />
                                </button>
                            </div>
                        </div>

                        <div className="modal-body modal-v2-body oct_alert-v2-modal-body oct_auth-user-modal-body">
                            <form className="oct_auth-user-form modal-v2-form" onSubmit={(event) => {
                                event.preventDefault();
                                submit();
                            }}>
                                <div className="oct_auth-user-form-grid device-auth-rule-form-grid">
                                    <div className="form-group oct_auth-user-field">
                                        <div className="oct_auth-user-form-group">
                                        <label className="oct_auth-user-label">Device<RequiredMark /></label>
                                        <SelectV2
                                            inputId="authorizationDevice"
                                            className="oct_auth-user-groups-select device-auth-single-select"
                                            value={form.deviceId}
                                            onChange={(value) => setValue("deviceId", value)}
                                            options={deviceOptions}
                                            placeholder="Choose device"
                                            isDisabled={!!currentDeviceId}
                                        />
                                        </div>
                                    </div>

                                    <div className="form-group oct_auth-user-field device-auth-field-full">
                                        <div className="oct_auth-user-form-group">
                                            <label className="oct_auth-user-label">Users<RequiredMark /></label>
                                            <CreatableSelect
                                                inputId="authorizationUsers"
                                                instanceId="authorizationUsers"
                                                className="oct_selectsearch-v2 oct_auth-user-groups-select"
                                                classNamePrefix="oct-selectsearchsub-v2"
                                                value={selectedUserOptions}
                                                onChange={(options) => setValue("usernames", (options || []).map(option => String(option.value || "").trim()).filter(Boolean))}
                                                options={[]}
                                                isMulti
                                                isSearchable
                                                isClearable
                                                closeMenuOnSelect={false}
                                                menuPosition="fixed"
                                                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                                menuShouldScrollIntoView={false}
                                                placeholder="Type username and press Enter"
                                                noOptionsMessage={() => "Type username and press Enter"}
                                                formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group oct_auth-user-field device-auth-field-full">
                                        <div className="oct_auth-user-form-group">
                                        <label className="oct_auth-user-label">Actions<RequiredMark /></label>
                                        <div className="device-auth-any-action-field">
                                            <input className="form-control oct_auth-user-input" value="Any Action" disabled />
                                            <button
                                                type="button"
                                                className="device-auth-actions-info-trigger"
                                                aria-label="Show included actions"
                                                onClick={() => setShowActionsInfo(current => !current)}
                                            >
                                                <MoreVertical size={18} />
                                            </button>
                                            {showActionsInfo && (
                                                <div className="device-auth-actions-info-box">
                                                    <div className="device-auth-actions-info-title">Included in Any Action</div>
                                                    {selectedDeviceActions.length ? (
                                                        <div className="device-auth-actions-info-list">
                                                            {selectedDeviceActions.map(action => (
                                                                <span className="device-auth-actions-info-pill" key={action}>{action}</span>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <div className="device-auth-actions-info-empty">
                                                            No syslog actions are selected for this device.
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        </div>
                                    </div>

                                    <div className="form-group oct_auth-user-field device-auth-field-full">
                                        <div className="oct_auth-user-form-group">
                                            <label className="oct_auth-user-label">Action Authorization<RequiredMark /></label>
                                            <div className="device-auth-action-policy-list">
                                                {form.actions.map(action => (
                                                    <div className="device-auth-action-policy-row" key={action}>
                                                        <span className="device-auth-action-policy-name">{action}</span>
                                                        <div className="device-auth-action-policy-toggle">
                                                            {AUTHORIZATION_VALUES.map(value => (
                                                                <button
                                                                    type="button"
                                                                    key={`${action}-${value}`}
                                                                    className={`device-auth-action-policy-option ${form.actionAuthorizations[action] === value ? "is-selected" : ""} ${value === "ALLOWED" ? "is-allowed" : "is-blocked"}`}
                                                                    onClick={() => setActionAuthorization(action, value)}
                                                                >
                                                                    {value === "ALLOWED" ? "Allowed" : "Denied"}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-group oct_auth-user-field device-auth-field-full">
                                        <div className="oct_auth-user-form-group">
                                        <label className="oct_auth-user-label">Reason</label>
                                        <textarea className="form-control oct_auth-user-input device-auth-reason-input"
                                            value={form.reason}
                                            onChange={(event) => setValue("reason", event.target.value)}
                                            rows={3}
                                            placeholder="Optional note" />
                                        </div>
                                    </div>

                                    <div className="form-group oct_auth-user-field device-auth-field-full">
                                        <div className="oct_check oct_auth-user-check">
                                            <label className="oct_check-label oct_auth-user-check-label">
                                                <input type="checkbox"
                                                    checked={!!form.alertEnabled}
                                                    onChange={() => setValue("alertEnabled", !form.alertEnabled)} />
                                                <span>Generate alert when this rule is violated</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>

                        <div className="modal-footer modal-v2-footer oct_alert-v2-modal-footer">
                            <div className="modal-v2-footer-actions oct_alert-v2-modal-actions">
                                <button type="button" className="btn modal-v2-btn modal-v2-btn-outline oct_alert-v2-modal-btn" onClick={onClose}>
                                    Cancel
                                </button>
                                <button type="button" className="btn modal-v2-btn modal-v2-btn-primary oct_alert-v2-modal-btn"
                                    disabled={saving}
                                    onClick={submit}>
                                    {saving ? <Spinner /> : "Save"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DeviceAuthorizationModal;
